/**
 * LedgerTab — лицевой счёт клиники: сколько на счёте и откуда взялось.
 *
 * Вкладка «Платежи» отвечает на вопрос «что нам пришло от клиента»,
 * эта — «сколько у клиники сейчас денег». Вопросы разные: абонентка
 * списывается со счёта, а приходит на счёт и картой, и переводом по
 * безналу, который провести автоматом нечем — отсюда ручная операция.
 *
 * Сама вкладка только достаёт данные и разводит три части: остаток,
 * журнал и форму ручной операции.
 */

import {useState} from 'react';
import {useQuery} from '@tanstack/react-query';

import {LEDGER_PAGE_SIZE, ledgerQueryOptions} from '@services/ledger';
import {Button} from '@components/ui/Button';
import {Loader} from '@components/ui/Loader';
import {extractErrorMessage} from '@utils/httpError';

import {LedgerBalance} from '../LedgerBalance';
import {LedgerJournal} from '../LedgerJournal';
import {ManualEntryModal} from '../ManualEntryModal';

type Props = {clientId: string; clientName: string; onSaved: (text: string) => void};

export const LedgerTab = ({clientId, clientName, onSaved}: Props) => {
  const [page, setPage] = useState(1);
  const [entryOpen, setEntryOpen] = useState(false);
  const {data, isPending, isError, error, refetch, isFetching} = useQuery(
    ledgerQueryOptions(clientId, page),
  );

  const pages = data
    ? Math.max(1, Math.ceil(data.totalCount / (data.pageSize || LEDGER_PAGE_SIZE)))
    : 1;

  /* После ручной операции возвращаемся на первую страницу: новая
     запись легла наверх, а человек мог смотреть пятую. */
  const handleSaved = (text: string) => {
    setEntryOpen(false);
    setPage(1);
    onSaved(text);
  };

  if (isPending) {
    return (
      <div className="flex min-h-[220px] items-center justify-center">
        <Loader label="Поднимаем счёт…" />
      </div>
    );
  }

  /* Про деньги молчать нельзя: пустой экран или вечный лоадер здесь
     читались бы как «операций нет» и «сейчас догрузится», а на деле
     остаток неизвестен. */
  if (isError || !data) {
    return (
      <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 text-center">
        <div className="text-[13px] text-destructive">
          {extractErrorMessage(error, 'Не удалось поднять счёт')}
        </div>
        <Button variant="outline" className="h-9 rounded-[9px]" onClick={() => void refetch()}>
          {isFetching ? 'Пробуем…' : 'Повторить'}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3.5">
      <LedgerBalance balanceTiyin={data.balanceTiyin} onManualEntry={() => setEntryOpen(true)} />

      <LedgerJournal
        list={data.list}
        totalCount={data.totalCount}
        page={page}
        pages={pages}
        onPage={setPage}
      />

      <ManualEntryModal
        open={entryOpen}
        clientId={clientId}
        clientName={clientName}
        balanceTiyin={data.balanceTiyin}
        onClose={() => setEntryOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  );
};
